/**
 * A markdown body, parsed.
 *
 * ONE parser reads markdown, and it is the one the view draws with: the view's
 * renderer is built on these same libraries, at the version the lockfile
 * resolves for both. So the outline, the text a search reads and the drawn
 * page all come from the same tree, and a question about what a body holds has
 * one answer. Nothing here reads markdown's structure any other way.
 *
 * The walks below keep their own stack instead of calling themselves. A body
 * is written by anybody, and one that nests a quote inside a quote ten
 * thousand deep is a body: a walk that recurses runs out of stack on it, and
 * a search that throws on one row answers for none.
 */

import type { Nodes, Root } from 'mdast'
import { fromMarkdown } from 'mdast-util-from-markdown'
import { gfmFromMarkdown } from 'mdast-util-gfm'
import { gfm } from 'micromark-extension-gfm'

export type MarkdownTree = Root
export type MarkdownNode = Nodes

/** The exact size of a parsed markdown tree. */
export type MarkdownTreeSize = {
  nodes: number
  depth: number
  breadth: number
  tableColumns: number
}

/**
 * Syntax markdown accepts beyond CommonMark, named for what it is.
 *
 * `gfm` is tables, strikethrough, task lists, footnotes and bare links. The
 * parse below and the view's adapter both read this list and map each name to
 * what their own library calls it, so the list is what decides what a body
 * may hold and no renderer's defaults do.
 */
export const MARKDOWN_EXTENSIONS = ['gfm'] as const

export type MarkdownExtension = (typeof MARKDOWN_EXTENSIONS)[number]

type ParseOptions = NonNullable<Parameters<typeof fromMarkdown>[1]>

/** What this parser calls each extension: how it is read, and how it is built. */
const EXTENSIONS: Record<
  MarkdownExtension,
  {
    syntax: NonNullable<ParseOptions['extensions']>[number]
    tree: NonNullable<ParseOptions['mdastExtensions']>[number]
  }
> = {
  gfm: { syntax: gfm(), tree: gfmFromMarkdown() },
}

/**
 * The tree of a body, read exactly as written, with every picture already its
 * words — the tree the page draws.
 */
export function parseMarkdown(body: string): MarkdownTree {
  const tree = fromMarkdown(body, {
    extensions: MARKDOWN_EXTENSIONS.map((name) => EXTENSIONS[name].syntax),
    mdastExtensions: MARKDOWN_EXTENSIONS.map((name) => EXTENSIONS[name].tree),
  })
  picturesAsWords(tree)
  return tree
}

/**
 * Measure the work a renderer would be handed.
 *
 * This deliberately walks the parsed tree rather than guessing at markdown's
 * grammar from its source. It is iterative because measuring a tree too deep
 * to draw must itself remain safe.
 */
export function markdownTreeSize(tree: MarkdownTree): MarkdownTreeSize {
  let nodes = 0
  let depth = 0
  let breadth = 0
  let tableColumns = 0
  const waiting: { node: MarkdownNode; depth: number }[] = [
    { node: tree, depth: 1 },
  ]
  for (let at = waiting.pop(); at; at = waiting.pop()) {
    nodes++
    depth = Math.max(depth, at.depth)
    const children = childrenOf(at.node)
    breadth = Math.max(breadth, children.length)
    if (at.node.type === 'tableRow') {
      tableColumns = Math.max(tableColumns, children.length)
    }
    for (let i = children.length - 1; i >= 0; i--) {
      waiting.push({ node: children[i], depth: at.depth + 1 })
    }
  }
  return { nodes, depth, breadth, tableColumns }
}

const childrenOf = (node: MarkdownNode): readonly MarkdownNode[] =>
  'children' in node ? node.children : []

/**
 * Every picture in a tree turned into the words it is drawn as: its
 * description, then its address.
 *
 * A picture is never fetched — drawing one is a request the READER's browser
 * makes, to whatever address the text names. So what the page shows of one is
 * words, and those words are decided here, once, in the tree: the view draws
 * this tree, and the outline, the search text and the direction of a block are
 * all read off it, so none of them can name a picture differently from the
 * page. The address is the one the body wrote; a picture named through a
 * reference takes the first definition of that name, as the page resolves it.
 */
export function picturesAsWords(tree: MarkdownTree): void {
  const addresses = new Map<string, string>()
  for (const { node } of nodesOf(tree)) {
    if (node.type !== 'definition') continue
    const name = node.identifier.toUpperCase()
    if (!addresses.has(name)) addresses.set(name, node.url)
  }
  for (const { node } of nodesOf(tree)) {
    if (!('children' in node)) continue
    const children: MarkdownNode[] = node.children
    children.forEach((child, i) => {
      if (child.type !== 'image' && child.type !== 'imageReference') return
      const address =
        child.type === 'image'
          ? child.url
          : addresses.get(child.identifier.toUpperCase())
      children[i] = {
        type: 'text',
        value: [child.alt, address].filter(Boolean).join(' '),
        position: child.position,
      }
    })
  }
}

/** A node and the list or table it sits in, if it sits in one. */
export type Visited = { node: MarkdownNode; within: MarkdownNode | null }

/**
 * Every node under `root`, in the order the body is read.
 *
 * `whole` names the nodes taken whole: one is yielded and what is inside it is
 * left to whoever asked. Each node comes with the nearest list or table around
 * it, which is what says two blocks are rows of one thing.
 */
export function* nodesOf(
  root: MarkdownNode,
  whole: (node: MarkdownNode) => boolean = () => false,
): Generator<Visited> {
  const waiting: Visited[] = [{ node: root, within: null }]
  for (let at = waiting.pop(); at; at = waiting.pop()) {
    yield at
    if (whole(at.node)) continue
    const children = childrenOf(at.node)
    const within =
      at.node.type === 'list' || at.node.type === 'table' ? at.node : at.within
    for (let i = children.length - 1; i >= 0; i--) {
      waiting.push({ node: children[i], within })
    }
  }
}

/**
 * The words a node holds, with the marks on them taken away.
 *
 * Read off the nodes, so what counts as a word is the parser's answer: an
 * entity is the character it names, a link is its text and never its address,
 * and code is kept exactly as written. Raw HTML counts as the characters
 * typed, because the view draws it as text and never runs it. A picture is
 * words by the time a tree is read (`picturesAsWords`); one met here anyway
 * counts as those same words, as far as the node alone says them. A
 * footnote's marker is a number the page assigns, not a word anybody wrote.
 */
export function wordsOf(root: MarkdownNode): string {
  let out = ''
  const waiting: MarkdownNode[] = [root]
  for (let node = waiting.pop(); node; node = waiting.pop()) {
    if (
      node.type === 'text' ||
      node.type === 'inlineCode' ||
      node.type === 'html' ||
      node.type === 'code'
    ) {
      out += node.value
    } else if (node.type === 'image') {
      out += [node.alt, node.url].filter(Boolean).join(' ')
    } else if (node.type === 'imageReference') {
      out += node.alt ?? ''
    } else if (node.type === 'break') {
      out += '\n'
    } else if (node.type !== 'footnoteReference') {
      const children = childrenOf(node)
      for (let i = children.length - 1; i >= 0; i--) waiting.push(children[i])
    }
  }
  return out
}
